import { useEffect, useId, useRef, useState } from 'react';
import { api, type ApiError, type FacilitySearchRow } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { ErrorNote } from '../ErrorNote';
import { sx } from './designStyles';

/** Wait this long after the last key before asking the server. */
export const SEARCH_DEBOUNCE_MS = 250;
/** The design's page size. The picker never loads the full list. */
export const SEARCH_LIMIT = 20;

/**
 * Server-side facility search (GET /api/aithora/facilities/search?q=&limit=20). Type to
 * search; nothing is fetched until there is something typed, and the full catalog is
 * never loaded. The server scopes the answer: a Facility Admin only ever finds their own
 * building. Keyboard: arrows move, Enter picks, Escape closes.
 */
export function FacilityPicker({
  onPick,
  onClear,
  label = 'Facility',
  placeholder = 'Search facilities by name or CCN',
  minChars = 1,
  initialValue = '',
  hideLabel = false,
  showMeta = true,
  wrapStyle,
  inputStyle,
  listStyle,
  optionStyle,
}: {
  onPick: (row: FacilitySearchRow) => void;
  /** The field was emptied (a filter going back to every facility). */
  onClear?: () => void;
  label?: string;
  placeholder?: string;
  minChars?: number;
  /** The text in the field to start with (the facility already picked). */
  initialValue?: string;
  /** Keep the label for screen readers only (the design's filters carry none). */
  hideLabel?: boolean;
  /** The CCN beside each name. */
  showMeta?: boolean;
  /**
   * The page's own design, as the design's CSS strings (sx): each one replaces the
   * picker's default look for that part (e.g. Oversight's 230px filter,
   * 'box-sizing:border-box;width:230px;min-height:34px;...').
   */
  wrapStyle?: string;
  inputStyle?: string;
  listStyle?: string;
  optionStyle?: (active: boolean) => string;
}) {
  const { client } = useSession();
  const id = useId();
  const [q, setQ] = useState(initialValue);
  const [rows, setRows] = useState<FacilitySearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < minChars) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const t = window.setTimeout(() => {
      api
        .facilitySearch(client, { query: { q: term, limit: SEARCH_LIMIT }, signal: ctrl.signal })
        .then((r) => {
          if (ctrl.signal.aborted) return;
          setRows(r.rows);
          setError(null);
          setActive(0);
        })
        .catch((e: unknown) => {
          if (ctrl.signal.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
          setError(e as ApiError);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [q, client, minChars, tick]);

  const pick = (r: FacilitySearchRow) => {
    onPick(r);
    setQ(r.name);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(rows.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && open && rows[active]) {
      e.preventDefault();
      pick(rows[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const listId = `${id}-list`;
  const term = q.trim();
  const showList = open && term.length >= minChars;
  return (
    <div className={wrapStyle === undefined ? 'picker' : undefined} style={wrapStyle === undefined ? undefined : sx(wrapStyle)}>
      <label className={hideLabel ? 'sr-only' : 'field__label'} htmlFor={`${id}-input`}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={`${id}-input`}
        className={inputStyle === undefined ? 'field__input picker__input' : undefined}
        style={inputStyle === undefined ? undefined : sx(inputStyle)}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && rows[active] ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onClear?.();
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKey}
      />
      {showList && (
        <ul
          className={listStyle === undefined ? 'picker__list' : undefined}
          style={listStyle === undefined ? undefined : sx(listStyle)}
          id={listId}
          role="listbox"
          aria-label={`${label} results`}
          aria-busy={loading}
        >
          {loading && rows.length === 0 && <li className="picker__note">Searching{'…'}</li>}
          {!loading && !error && rows.length === 0 && <li className="picker__note">No facility matches {'“'}{term}{'”'}.</li>}
          {rows.map((r, i) => (
            <li
              key={r.id}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={optionStyle === undefined ? `picker__opt${i === active ? ' picker__opt--active' : ''}` : undefined}
              style={optionStyle === undefined ? undefined : sx(optionStyle(i === active))}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className={optionStyle === undefined ? 'picker__name' : undefined}>{r.name}</span>
              {showMeta && <span className="picker__meta">{r.ccn ? `CCN ${r.ccn}` : 'No CCN'}</span>}
            </li>
          ))}
          {rows.length === SEARCH_LIMIT && <li className="picker__note">Showing the first {SEARCH_LIMIT}. Type more to narrow it.</li>}
        </ul>
      )}
      {error && <ErrorNote error={error} onRetry={() => setTick((t) => t + 1)} compact />}
    </div>
  );
}
